import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Site, GatewayLogType, NotificationType } from './types';
import { LANGUAGE_OPTIONS } from './common/constants';
import { useAppDispatch } from '../app/hooks';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

import { store } from '../app/store';

import { DataState } from '../app/data-slice';

import { CONTEXT_CONTAINERS, ctx_setSize } from '../components/menus/context-menu-slice';

//  --------------------------------------------------------------------------
//
//  P U B L I C   F U N C T I O N S
//
//  --------------------------------------------------------------------------

// ------------------------------------------------------------
//
//  Keep shortening a text string until it does not cause an
//  overflow in its container.
//
//  Notes on params:
//
//    retainTrailingNumber: if true, causes an abbreviation
//        of the form 'abcde...123', that retains trailing
//        numbers.
//    pageVisible: states whether or not the page hosting this
//        component is currently visible. A change in
//        visibility causes useLayoutEffect to re-fire, and
//        re-check for overflow.
//
// ------------------------------------------------------------

export function AbbreviatedText(props: {
  text: string;
  retainTrailingNumbers?: boolean;
  pageVisible?: boolean;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  const [sText, setText] = useState<string>(props.text);
  const rTextWithoutDots = useRef<string>(props.text);
  const monitorDivForOverflow = useRef<HTMLDivElement | null>(null);

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  useLayoutEffect(() => {
    //
    const { current } = monitorDivForOverflow;
    if (current) {
      //
      const { clientWidth, scrollWidth, clientHeight, scrollHeight } = current;

      // an overflow has occurred if we need to scroll in either the X or Y direction.
      const hasOverflow: boolean =
        scrollHeight > clientHeight || scrollWidth > clientWidth;

      // shorten the text if it is overflowing.
      if (hasOverflow === true) {
        //
        shortenText({
          setText: setText,
          textWithoutDots: rTextWithoutDots,
          retainTrailingNumbers: props.retainTrailingNumbers,
        });
        //
      }
      //
    }
    //
  }, [monitorDivForOverflow, sText, props.pageVisible, props.retainTrailingNumbers]);

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      ref={monitorDivForOverflow}
      style={{
        overflowX: 'auto',
      }}
    >
      {sText}
    </div>
  );
  //
} // AbbreviatedText

//-------------------------------------------------
//
//  Helper function to replace newline delimiters
//  with HTML line breaks.
//
//-------------------------------------------------

export const addLineBreaks = (text: string | undefined) => {
  //
  if (text === undefined) {
    return;
  }

  return text.split('\n').map((substr: string, i: number) => (
    <>
      {substr}
      <br />
    </>
  ));
  //
}; // addLineBreaks

//  ------------------------------------------------------------
//
//  Determines the prefix to apply to all API calls.
//
//  ------------------------------------------------------------

export function apiPrefix() {
  //
  var currentUrl: string = getBaseURL();
  var addPort: boolean = false;

  // check if there is a port number at the end.
  if (nthIndexOf({ inputString: currentUrl, findString: ':', itemNumber: 2 }) >= 0) {
    currentUrl = currentUrl.slice(
      0,
      nthIndexOf({ inputString: currentUrl, findString: ':', itemNumber: 2 }),
    );
    addPort = true;
  }

  // if we're using localhost then we've probably got a port number (likely 3000), which should be replaced with port 8080.
  // if we're on one of the production servers then there is no port number included, but we need to append '/tannet-api' to reach the APIs.
  var apiPrefix: string = currentUrl;
  if (addPort === true) apiPrefix = apiPrefix + ':8080';
  else apiPrefix = apiPrefix + '/tannet-api';

  // return something.
  return apiPrefix;
  //
} // apiPrefix

//  ------------------------------------------------------------
//
//  Build a url from a given prefix, host, path and port.
//
//  ------------------------------------------------------------

export function buildUrl(args: {
  prefix: string | undefined;
  host: string | undefined;
  path: string | undefined;
  port: number | undefined;
}) {
  //
  let url: string = '';

  if (args.prefix !== undefined) {
    url = url + args.prefix;
  } else {
    url = url + 'http';
  }
  if (!url.endsWith('://')) url = url + '://';
  if (args.host !== undefined) {
    url = url + args.host;
  } else {
    url = url + 'unknown-host';
  }
  if (args.port !== undefined) {
    url = url + ':' + args.port.toString();
  }
  if (args.path !== undefined) {
    url = url + args.path;
  }

  return url;
  //
} // buildUrl

// ------------------------------------------------------------
//
//	Define a hook that monitors the container, and
//	keeps the current size in state.
//
// ------------------------------------------------------------

export function ContainerHook(props: { container: CONTEXT_CONTAINERS }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  //  ------------------------------------------------------------
  //
  //  code for storing the size when the data window is resized.
  //
  //  ------------------------------------------------------------

  // handle changes in size to the data window container.
  const resizeDataWindow = React.useRef<ResizeObserver>(
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      //
      // update the size of the container
      if (entries[0].contentRect.width > 0 && entries[0].contentRect.height > 0) {
        dispatch(
          ctx_setSize({
            container: props.container,
            size: {
              x: entries[0].contentRect.width,
              y: entries[0].contentRect.height,
            },
          }),
        );
      }
      //
    }),
  );

  // create a reference to the HTML Div element that contains the data items, so that we can monitor for changes in size.
  const dataWindow = React.useCallback(
    (container: HTMLDivElement | null) => {
      //
      // check that this reference has actually been assigned to an element, and switch on observing.
      if (container !== null) {
        resizeDataWindow.current.observe(container);
      }

      // When element is unmounted, ref callback is called with a null argument
      // => best time to cleanup the observer
      else if (resizeDataWindow.current) {
        resizeDataWindow.current.disconnect();
      }
      //
    },
    [resizeDataWindow.current],
  );

  // return the reference
  return dataWindow;
  //
} // ContainerHook

//  ------------------------------------------------------------
//
//  Displays a file size in either bytes, KB, MB, GB, or TB.
//
//  ------------------------------------------------------------

export function displayFileSize(args: { bytes: number }) {
  //
  var textValue: string = '';

  if (args.bytes !== null) {
    if (args.bytes < 0) textValue = '/';
    else if (args.bytes < 1024) textValue = args.bytes.toFixed(2) + ' B';
    else if (args.bytes < 1024 * 1024) textValue = (args.bytes / 1024).toFixed(2) + ' KB';
    else if (args.bytes < 1024 * 1024 * 1024)
      textValue = (args.bytes / (1024 * 1024)).toFixed(2) + ' MB';
    else if (args.bytes < 1024 * 1024 * 1024 * 1024)
      textValue = (args.bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    else textValue = (args.bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2) + ' TB';
  } else textValue = 'size is null';

  // return something.
  return textValue;
  //
} // displayFileSize

//  ------------------------------------------------------------
//
//  Extracts the namespace and name from the
//  access_url
//
//  ------------------------------------------------------------

export function extractFileFromAccessUrl(args: { accessUrl: string }) {
  //
  var namespace: string = '';
  var filename: string = '';
  if (args.accessUrl.indexOf('id=') > -1) {
    //
    // get the rest of the URL.
    var remainder: string = args.accessUrl.slice(
      args.accessUrl.indexOf('id=') + 3 - args.accessUrl.length,
    );
    if (remainder.indexOf(':') > -1) {
      namespace = remainder.slice(0, remainder.indexOf(':'));
      filename = remainder.slice(remainder.indexOf(':') + 1 - remainder.length);
    } else {
      filename = remainder;
    }
    //
  }

  // return something.
  return {
    namespace: namespace,
    filename: filename,
  };
  //
} // extractFileFromAccessUrl

//  ------------------------------------------------------------
//
//  Extracts the namespace and name from the
//  obs_publisher_did
//
//  ------------------------------------------------------------

export function extractFileFromObsPublisherDid(args: { obsPublisherDid: string }) {
  //
  var namespace: string = '';
  var filename: string = '';
  if (args.obsPublisherDid.indexOf('?') > -1) {
    //
    // get the rest of the URL.
    var remainder: string = args.obsPublisherDid.slice(
      args.obsPublisherDid.indexOf('?') + 1 - args.obsPublisherDid.length,
    );
    if (remainder.indexOf(':') > -1) {
      namespace = remainder.slice(0, remainder.indexOf(':'));
      filename = remainder.slice(remainder.indexOf(':') + 1 - remainder.length);
    } else {
      filename = remainder;
    }
    //
  }

  // return something.
  return {
    namespace: namespace,
    filename: filename,
  };
  //
} // extractFileFromObsPublisherDid

//  ------------------------------------------------------------
//
//  Extracts the namespace and name from the obs_id
//
//  ------------------------------------------------------------

export function extractFileFromObsID(args: { obsID: string }) {
  //
  var namespace: string = '';
  var filename: string = '';
  if (args.obsID.indexOf(':') > -1) {
    //
    namespace = args.obsID.slice(0, args.obsID.indexOf(':'));
    filename = args.obsID.slice(args.obsID.indexOf(':') + 1 - args.obsID.length);
  } else {
    filename = args.obsID;
  }

  // return something.
  return {
    namespace: namespace,
    filename: filename,
  };
  //
} // extractFileFromObsID

//  -------------------------------------------------
//
//  Finds the index of the user's language in the
//  language dropdown box.
//
//  -------------------------------------------------

export function findLanguageIndex(args: { language: string | null }) {
  //
  const lang = args.language;

  if (!lang) {
    return 0;
  }

  const index = LANGUAGE_OPTIONS.findIndex((element) => element.value === lang);

  return index === -1 ? 0 : index;
  //
} // findLanguageIndex

//	------------------------------------------------------------
//
//	Assign a user-readable name to a compute service.
//
//	------------------------------------------------------------

export function fn_serviceName(args: { serviceType: string }) {
  //
  switch (args.serviceType.toLowerCase()) {
    case 'carta':
      return 'CARTA';
    case 'notebook':
      return 'Notebook';
    case 'jupyterhub':
      return 'JupyterHUB';
    case 'desktop':
      return 'Virtual Desktop';
    case 'headless':
      return 'Headless';
    default:
      return 'Unknown service';
  }
  //
} // fn_serviceName

//  -------------------------------------------------
//
//  Removes double quotes from around a string.
//
//  -------------------------------------------------

export function fn_stripDoubleQuotes(args: { value: string | null }) {
  //
  var outValue: string = '';
  if (args.value !== null)
    if (args.value[0] === '"' && args.value[args.value.length - 1] === '"')
      outValue = args.value.slice(1, args.value.length - 1);

  // return something.
  return outValue;
  //
} // fn_stripDoubleQuotes

//  ------------------------------------------------------------
//
//  update the width, height, colour, and stroke width of
//  an SVG icon.
//
//  ------------------------------------------------------------

export function fn_updateIcon(args: {
  original: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
  colour?: string;
  uriEncode?: boolean;
}) {
  //
  let updated: string = args.original;

  if (args.width !== undefined) {
    updated = updated.replaceAll('{WIDTH}', args.width.toString());
  }
  if (args.height !== undefined) {
    updated = updated.replaceAll('{HEIGHT}', args.height.toString());
  }
  if (args.strokeWidth !== undefined) {
    updated = updated.replaceAll('{STROKE-WIDTH}', args.strokeWidth.toString());
  }
  if (args.colour !== undefined) {
    updated = updated.replaceAll('{COLOUR}', args.colour);
  }

  if (args.uriEncode === true) {
    return encodeURIComponent(updated);
  } else {
    return updated;
  }
  //
} // fn_updateIcon

//  ------------------------------------------------------------
//
//  Gets the current URL. i.e. https://domain:port
//
//  ------------------------------------------------------------

export function getBaseURL() {
  //
  const { protocol, hostname, port } = window.location;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  //
} // getBaseURL

//  ------------------------------------------------------------
//
//  Gets the user's language from the browser
//  settings. If the language is hyphenated, i.e. en-US,
//  then we will only use the bit from before the hyphen.
//
//  ------------------------------------------------------------

export function getBrowserDefaultLanguage(args: { defaultLang: string }) {
  //
  // get the browser's language.
  var browserLanguage = navigator.language;

  var language: string = args.defaultLang;
  if (browserLanguage !== '') {
    // extract the characters before the first '-'.
    const hyphenPos = browserLanguage.indexOf('-');
    if (hyphenPos > -1) language = browserLanguage.slice(0, hyphenPos);
    else language = browserLanguage;
  }

  // return something.
  return language;
  //
} // getBrowserDefaultLanguage

//  ------------------------------------------------------------
//
//  Get the current time in ISO format.
//
//  ------------------------------------------------------------

export function getISOTime() {
  //
  const currentTime = new Date(Date.now());
  let isoTime: string = currentTime.toISOString();
  if (isoTime.length > 19) {
    isoTime = isoTime.slice(0, 19);
  }

  // return something.
  return isoTime;
  //
} // getISOTime

//  ------------------------------------------------------------
//
//  Get the true number of logs in a given list by summing the
//  logs and their duplicates.
//
//  ------------------------------------------------------------

export function getLogsCount(logs: GatewayLogType[]) {
  //
  let count = 0;
  for (const log of logs) {
    if (log.metadata.repeat_count !== undefined) {
      count += log.metadata.repeat_count + 1;
    }
  }

  return count;
  //
} // getLogsCount

//  -------------------------------------------------
//
//  search through the storages state array to find
//  this uuid.
//
//  -------------------------------------------------

export function getStorageAreaFromID(args: { storageAreaUUID: string }) {
  //
  var storageArea: string = args.storageAreaUUID;
  var site: Site | undefined = undefined;

  const stateFetch: any = store.getState();
  const stateData: DataState = stateFetch.data;

  for (var siteIdx = 0; siteIdx < stateData.storageAreas?.length ?? 0; siteIdx++)
    for (
      var areaIdx = 0;
      areaIdx < stateData.storageAreas[siteIdx].storage_areas.length;
      areaIdx++
    )
      if (
        stateData.storageAreas[siteIdx].storage_areas[areaIdx].storage_id ===
        args.storageAreaUUID
      ) {
        storageArea = stateData.storageAreas[siteIdx].storage_areas[areaIdx].name;
        site = stateData.storageAreas[siteIdx].site;
      }

  // return something.
  return { storageArea: storageArea, site: site };
  //
} // getStorageAreaFromID

//  -------------------------------------------------
//
//  Function to check Session Validity
//
//  -------------------------------------------------

export async function hasSessionExpired() {
  //
  const response = await fetch(apiPrefix() + '/v1/check_session', {
    credentials: 'include',
  });
  const gateway_session = await response.json();

  return gateway_session.is_valid;
  //
} // hasSessionExpired

//  ------------------------------------------------------------
//
//  Monitors for mouse clicks so that a dropdown menu
//  attached to {ref} can be closed.
//
//  ------------------------------------------------------------

export function HideDropdownMenu(args: {
  sDropdownDisplayed: boolean | undefined;
  setDropdownDisplayed: any;
  redux?: boolean;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const ref = useRef<HTMLDivElement>(null);

  //  ------------------------------------------------------------
  //
  //  handle click events, and check if we've clicked on
  //  the dropdown menu, or the previous cell, which should
  //  be the dropdown button.
  //
  //  ------------------------------------------------------------

  const handleClickOutside = (event: any) => {
    var currentCell: boolean = false;
    var previousCell: boolean = false;

    if (ref.current) {
      currentCell = ref.current.contains(event.target);
      if (ref.current.previousSibling)
        previousCell = ref.current.previousSibling.contains(event.target);
    }
    if (currentCell === false && previousCell === false) {
      if (args.redux === true) {
        dispatch(args.setDropdownDisplayed(false));
      } else {
        args.setDropdownDisplayed(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  return { ref };
  //
} // HideDropdownMenu

//  ------------------------------------------------------------
//
//  build the site name. we prefix the node if it is not
//  already prefixed to the site name.
//
//  ------------------------------------------------------------

export function siteName(args: { site?: Site }) {
  //
  let name: string = 'unknown site';

  if (args.site !== undefined) {
    //
    name = args.site.site ?? '';
    const node = args.site.node?.toUpperCase() ?? '';
    const site = args.site.site?.toUpperCase() ?? '';

    if (!site.startsWith(node)) {
      name = node + ' ' + site;
    }
    //
  }

  return name;
  //
} // siteName

//  --------------------------------------------------------------------------
//
//  P R I V A T E   F U N C T I O N S
//
//  --------------------------------------------------------------------------

// ------------------------------------------------------------
//
//  Reduce a text string by one character.
//  We add the dots (...) to the section where the
//  character was removed.
//  We don't take off numbers on the right-hand side.
//
// ------------------------------------------------------------

function shortenText(args: {
  setText: any;
  textWithoutDots: any;
  retainTrailingNumbers?: boolean;
}) {
  //
  // shrink the text if there is an overflow.
  var text: string = args.textWithoutDots.current ?? '';
  const len: number = text.length;

  // look for the first non-numeric character from the right.
  var foundChar: boolean = false;
  var newText: string = '';
  var newTextWithoutDots: string = '';

  if (args.retainTrailingNumbers !== true) {
    foundChar = true;
    newText = text.substring(0, len - 1) + '...';
    newTextWithoutDots = text.substring(0, len - 1);
  } else {
    //
    if (len > 0) {
      for (var i = len - 1; i >= 0; i--) {
        var alpha: boolean = !(text[i] >= '0' && text[i] <= '9');
        if (alpha === false || foundChar === true) {
          newText = text[i] + newText;
          newTextWithoutDots = text[i] + newTextWithoutDots;
        }
        if (alpha === true && foundChar === false) {
          newText = '...' + newText;
          foundChar = true;
        }
      }
    }
  }
  if (newText !== text && newTextWithoutDots !== '') {
    args.setText(newText);
    args.textWithoutDots.current = newTextWithoutDots;
  }
  //
} // shortenText

//	-------------------------------------------------
//
//  P R I V A T E   F U N C T I O N S
//
//  --------------------------------------------------------------------------

//  ------------------------------------------------------------
//
//  find the position of the nth occurrence of a
//  substring within a string.
//
//  ------------------------------------------------------------

function nthIndexOf(args: {
  inputString: string;
  findString: string;
  itemNumber: number;
}) {
  //
  var i: number = -1;
  var n: number = args.itemNumber;
  while (n-- && i++ < args.inputString.length) {
    i = args.inputString.indexOf(args.findString, i);
    if (i < 0) break;
  }

  return i;
  //
} // nthIndexOf

//	-------------------------------------------------
//
//	count the number of notifications that are
//	unread.
//
//	-------------------------------------------------

export function numUnreadNotifications(args: { notifications: NotificationType[] }) {
  var foundUnread: number = 0;
  for (var i: number = 0; i < args.notifications.length; i++)
    if (args.notifications[i].readFlag === false) foundUnread = foundUnread + 1;

  // return something.
  return foundUnread;
} // numUnreadNotifications
